import { Heading, Stack, useToast, VStack, Spinner } from "@chakra-ui/react";
import { useKydEvent } from "../../hooks/useKydEvent";
import { useParams } from "react-router-dom";
import actions from "../../../../actions";
import { useCallback, useEffect, useState } from "react";
import EventSummarySection from "../../SummarySection/EventSummarySection";
import { createOnUpdateEvent, EditModalType } from "../../../../utils";
import { useDialog } from "../../../../Common/Dialog";
import { pollForEventUpdateStatus as pollForStatus } from "../../utils/poolForEventUpdateStatus";
import { PayoutsTable } from "./PayoutsTable";
import {
  EventDetailPayoutsSection,
  EventDetailSummarySection,
  EventDetailSummarySectionData,
} from "@common/interfaces/KYDEvent";

const summarySectionType = "summary";
const payoutsSectionType = "payouts";

export interface PayoutsNavSectionData {
  summary?: EventDetailSummarySection;
  payouts?: EventDetailPayoutsSection;
}

const PayoutsNavSection = () => {
  const { event_id } = useParams();
  const { kydEvent, refetch } = useKydEvent(event_id);
  const toast = useToast();
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState<PayoutsNavSectionData>();

  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const dialog = useDialog();

  const summarySection = data?.summary;
  const payoutsSection = data?.payouts;

  const fetchData = useCallback(async () => {
    if (!kydEvent?.id) return;

    setLoading(true);

    try {
      const response = await actions.ADMIN.fetchEventSections(kydEvent?.id, [
        summarySectionType,
        payoutsSectionType,
      ]);

      setData(response);
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [actions, kydEvent, setLoading]);

  const pollForEventUpdateStatus = useCallback(() => {
    return pollForStatus({
      onError: (text) => dialog({ text }),
      refetch: () => refetch({ silent: true }),
      event_id,
    });
  }, [event_id, dialog, refetch]);

  const onUpdateEvent = useCallback(
    createOnUpdateEvent({
      dialog,
      event_id,
      pollForEventUpdateStatus,
      refetch,
      setLoading: (isLoading) => setLoading(isLoading),
      setModalType,
      toast,
    }),
    [
      event_id,
      refetch,
      setLoading,
      pollForEventUpdateStatus,
      dialog,
      toast,
      setModalType,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Stack>
      {loading ? (
        <VStack w="100%">
          <Spinner />
        </VStack>
      ) : (
        <VStack align={"flex-start"} w="100%">
          {summarySection ? (
            <EventSummarySection
              isLoading={loading}
              data={summarySection?.data as EventDetailSummarySectionData}
              permissions={summarySection?.permissions || []}
              onUpdateEvent={onUpdateEvent}
              modalType={modalType}
              setModalType={setModalType}
              pollForEventUpdateStatus={pollForEventUpdateStatus}
              refetchEvent={refetch}
            />
          ) : null}

          <Stack
            direction={{ base: "column" }}
            justify={"space-between"}
            w="100%"
          >
            <Heading w="full" size="md">
              Payouts
            </Heading>
            {payoutsSection ? (
              <PayoutsTable data={payoutsSection.data} />
            ) : null}
          </Stack>
        </VStack>
      )}
    </Stack>
  );
};

export default PayoutsNavSection;
